import { useQuery } from "@tanstack/react-query";
import { GatewayService } from "../api/gateway.service";
import type { Client } from "@shared-types";

/** State and actions returned by {@link useClients}. */
export interface UseClientsReturn {
    clients: Client[];
    loading: boolean;
    error: Error | null;
    /** Re-run the clients query. */
    refetch: () => void;
}

/**
 * Fetch the full list of clients.
 *
 * @example
 * ```tsx
 * const { clients, loading } = useClients();
 * ```
 */
export const useClients = (): UseClientsReturn => {
    const { data, isLoading, error, refetch } = useQuery({
        queryKey: ["gateway", "clients"],
        queryFn: ({ signal }) => GatewayService.getClients({ signal }),
    });
    // react-query refetch returns Promise<QueryObserverResult>; our public
    // interface declares `() => void`. Wrap to drop the promise — callers
    // that want to await would have to widen the interface deliberately.
    return {
        clients: data ?? [],
        loading: isLoading,
        error,
        refetch: () => { void refetch(); },
    };
};
